// People and Membership administration (PER-001, PER-003..PER-005,
// PER-009..PER-011, PER-013, PER-014; ADM-009).
//
// Person and Membership stay visibly separate here: a proxy-only person is a real
// member of the circle without a sign-in, while the preset and capability controls
// edit the access grant. The server remains authority for both the `canManage` flag
// and every write.

import {
  ALL_CAPABILITIES,
  CAPABILITIES,
  people as contracts,
  type Capability,
} from "@herefor/contracts";
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Select,
  StatusMessage,
  TextInput,
} from "@herefor/design-system";
import {
  PEOPLE_MANAGEMENT_MESSAGES as COPY,
  PRACTICAL_UPDATE_CAPABILITY_MESSAGES,
} from "@herefor/product-language";
import { useState, type ChangeEvent, type SyntheticEvent } from "react";

export interface CreatePersonDraft {
  readonly chosenName: string;
  readonly relationshipLabel?: string;
  readonly preset: contracts.MembershipPreset;
  readonly capabilities: readonly Capability[];
  readonly contactMethods: readonly contracts.ContactMethodInput[];
}

export interface UpdatePersonDraft {
  readonly personId: string;
  readonly chosenName: string;
  readonly relationshipLabel: string | null;
}

export interface UpdateMembershipDraft {
  readonly personId: string;
  readonly preset: contracts.MembershipPreset;
  readonly capabilities: readonly Capability[];
}

export type PeopleOutcome =
  | { readonly status: "idle" }
  | { readonly status: "working" }
  | {
      readonly status: "saved";
      readonly action: "created" | "person" | "membership" | "deactivated";
    }
  | { readonly status: "refused"; readonly message: string };

export interface PeopleManagementProps {
  readonly directory: contracts.PeopleDirectoryView;
  readonly outcome: PeopleOutcome;
  readonly actionsDisabled?: boolean;
  readonly onCreate: (draft: CreatePersonDraft) => Promise<boolean>;
  readonly onUpdatePerson: (draft: UpdatePersonDraft) => Promise<boolean>;
  readonly onUpdateMembership: (draft: UpdateMembershipDraft) => Promise<boolean>;
  readonly onDeactivate: (personId: string) => Promise<boolean>;
}

type OpenDialog =
  | { readonly kind: "add" }
  | { readonly kind: "person"; readonly person: contracts.PersonMembershipView }
  | { readonly kind: "membership"; readonly person: contracts.PersonMembershipView }
  | { readonly kind: "deactivate"; readonly person: contracts.PersonMembershipView }
  | null;

const PRESET_OPTIONS = (["organiser", "helper", "viewsOnly"] as const).map((value) => ({
  value,
  label: COPY.presets[value],
}));

export function PeopleManagement({
  directory,
  outcome,
  onCreate,
  onUpdatePerson,
  onUpdateMembership,
  onDeactivate,
  actionsDisabled = false,
}: PeopleManagementProps) {
  if (!directory.canManage) {
    return <PeopleDirectory directory={directory} outcome={outcome} />;
  }

  return (
    <ManageablePeople
      directory={directory}
      actionsDisabled={actionsDisabled}
      onCreate={onCreate}
      onDeactivate={onDeactivate}
      onUpdateMembership={onUpdateMembership}
      onUpdatePerson={onUpdatePerson}
      outcome={outcome}
    />
  );
}

function ManageablePeople({
  directory,
  outcome,
  actionsDisabled = false,
  onCreate,
  onUpdatePerson,
  onUpdateMembership,
  onDeactivate,
}: PeopleManagementProps) {
  const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
  const busy = actionsDisabled || outcome.status === "working";

  return (
    <>
      <PeopleDirectory directory={directory} onOpen={setOpenDialog} outcome={outcome} />

      {openDialog?.kind === "add" ? (
        <AddPersonDialog
          busy={busy}
          onClose={() => {
            setOpenDialog(null);
          }}
          onCreate={async (draft) => {
            if (await onCreate(draft)) setOpenDialog(null);
          }}
        />
      ) : null}

      {openDialog?.kind === "person" ? (
        <EditPersonDialog
          busy={busy}
          onClose={() => {
            setOpenDialog(null);
          }}
          onSave={async (draft) => {
            if (await onUpdatePerson(draft)) setOpenDialog(null);
          }}
          person={openDialog.person}
        />
      ) : null}

      {openDialog?.kind === "membership" ? (
        <EditMembershipDialog
          busy={busy}
          onClose={() => {
            setOpenDialog(null);
          }}
          onSave={async (draft) => {
            if (await onUpdateMembership(draft)) setOpenDialog(null);
          }}
          person={openDialog.person}
        />
      ) : null}

      {openDialog?.kind === "deactivate" ? (
        <DeactivateDialog
          busy={busy}
          onClose={() => {
            setOpenDialog(null);
          }}
          onDeactivate={async (personId) => {
            if (await onDeactivate(personId)) setOpenDialog(null);
          }}
          person={openDialog.person}
        />
      ) : null}
    </>
  );
}

function PeopleDirectory({
  directory,
  outcome,
  onOpen,
}: {
  readonly directory: contracts.PeopleDirectoryView;
  readonly outcome: PeopleOutcome;
  readonly onOpen?: (dialog: Exclude<OpenDialog, null>) => void;
}) {
  return (
    <section className="people" data-people-manage={onOpen === undefined ? "denied" : "allowed"}>
      <div className="people__heading">
        <div>
          <h1>{COPY.heading}</h1>
          <p>{COPY.introduction}</p>
        </div>
        {onOpen === undefined ? null : (
          <Button
            onClick={() => {
              onOpen({ kind: "add" });
            }}
            variant="primary"
          >
            {COPY.addAction}
          </Button>
        )}
      </div>

      <OutcomeMessage outcome={outcome} />

      {directory.people.length === 0 ? (
        <EmptyState title={COPY.emptyTitle} description={COPY.emptyBody} />
      ) : (
        <div className="people__list">
          {directory.people.map((person) => (
            <PersonCard
              key={person.personId}
              {...(onOpen === undefined ? {} : { onOpen })}
              person={person}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function PersonCard({
  person,
  onOpen,
}: {
  readonly person: contracts.PersonMembershipView;
  readonly onOpen?: (dialog: Exclude<OpenDialog, null>) => void;
}) {
  return (
    <Card title={person.chosenName} titleLevel={2}>
      <div className="people__labels">
        <span className="people__label">{roleLabelFor(person)}</span>
        {person.proxyOnly ? <span className="people__label">{COPY.proxyOnlyLabel}</span> : null}
        {person.accessState !== "active" ? (
          <span className="people__label">{COPY.accessStates[person.accessState]}</span>
        ) : null}
      </div>

      {person.relationshipLabel === undefined ? null : (
        <p className="people__relationship">
          {COPY.relationshipLabel}: {person.relationshipLabel}
        </p>
      )}

      <div className="people__contacts">
        <p>{COPY.contactMethodsLabel}</p>
        {person.contactMethods.length === 0 ? (
          <p>{COPY.noContactMethods}</p>
        ) : (
          <ul>
            {person.contactMethods.map((contact) => (
              <li key={contact.contactMethodId}>
                {COPY.contactKinds[contact.kind]}: {contact.maskedDestination}
              </li>
            ))}
          </ul>
        )}
      </div>

      {onOpen !== undefined && person.accessState === "active" ? (
        <div className="people__actions">
          <Button
            onClick={() => {
              onOpen({ kind: "person", person });
            }}
          >
            {COPY.editPersonAction}
          </Button>
          <Button
            onClick={() => {
              onOpen({ kind: "membership", person });
            }}
          >
            {COPY.editCapabilitiesAction}
          </Button>
          <Button
            onClick={() => {
              onOpen({ kind: "deactivate", person });
            }}
            variant="danger"
          >
            {COPY.deactivateAction}
          </Button>
        </div>
      ) : null}
    </Card>
  );
}

function AddPersonDialog({
  busy,
  onClose,
  onCreate,
}: {
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onCreate: (draft: CreatePersonDraft) => Promise<void>;
}) {
  const [chosenName, setChosenName] = useState("");
  const [relationshipLabel, setRelationshipLabel] = useState("");
  const [email, setEmail] = useState("");
  const [sms, setSms] = useState("");
  const [preset, setPreset] = useState<contracts.MembershipPreset>("helper");
  const [capabilities, setCapabilities] = useState<readonly Capability[]>(
    contracts.capabilitiesForPreset("helper"),
  );

  const submit = (event: SyntheticEvent<HTMLFormElement, SubmitEvent>) => {
    event.preventDefault();
    void onCreate({
      chosenName,
      ...(relationshipLabel.trim().length === 0 ? {} : { relationshipLabel }),
      preset,
      capabilities,
      contactMethods: [
        ...(email.trim().length === 0 ? [] : [{ kind: "email" as const, destination: email }]),
        ...(sms.trim().length === 0 ? [] : [{ kind: "sms" as const, destination: sms }]),
      ],
    });
  };

  return (
    <Dialog
      footer={
        <>
          <Button disabled={busy} onClick={onClose}>
            {COPY.cancelAction}
          </Button>
          <Button disabled={busy} form="people-add-form" type="submit" variant="primary">
            {COPY.createAction}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={COPY.addTitle}
    >
      <form className="people__form" id="people-add-form" onSubmit={submit}>
        <TextInput
          label={COPY.nameLabel}
          onChange={(event) => {
            setChosenName(event.target.value);
          }}
          required
          value={chosenName}
        />
        <TextInput
          label={COPY.relationshipInputLabel}
          onChange={(event) => {
            setRelationshipLabel(event.target.value);
          }}
          value={relationshipLabel}
        />
        <TextInput
          label={COPY.emailLabel}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          type="email"
          value={email}
        />
        <TextInput
          hint={COPY.phoneHint}
          label={COPY.phoneLabel}
          onChange={(event) => {
            setSms(event.target.value);
          }}
          type="tel"
          value={sms}
        />
        <MembershipFields
          capabilities={capabilities}
          onCapabilitiesChange={setCapabilities}
          onPresetChange={(nextPreset) => {
            setPreset(nextPreset);
            setCapabilities(contracts.capabilitiesForPreset(nextPreset));
          }}
          preset={preset}
        />
        <StatusMessage tone="info">{COPY.proxyCreationNotice}</StatusMessage>
      </form>
    </Dialog>
  );
}

function EditPersonDialog({
  person,
  busy,
  onClose,
  onSave,
}: {
  readonly person: contracts.PersonMembershipView;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSave: (draft: UpdatePersonDraft) => Promise<void>;
}) {
  const [chosenName, setChosenName] = useState(person.chosenName);
  const [relationshipLabel, setRelationshipLabel] = useState(person.relationshipLabel ?? "");

  return (
    <Dialog
      footer={
        <>
          <Button disabled={busy} onClick={onClose}>
            {COPY.cancelAction}
          </Button>
          <Button disabled={busy} form="people-edit-form" type="submit" variant="primary">
            {COPY.saveAction}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={COPY.editPersonTitle}
    >
      <form
        className="people__form"
        id="people-edit-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({
            personId: person.personId,
            chosenName,
            relationshipLabel: relationshipLabel.trim().length === 0 ? null : relationshipLabel,
          });
        }}
      >
        <TextInput
          label={COPY.nameLabel}
          onChange={(event) => {
            setChosenName(event.target.value);
          }}
          required
          value={chosenName}
        />
        <TextInput
          label={COPY.relationshipInputLabel}
          onChange={(event) => {
            setRelationshipLabel(event.target.value);
          }}
          value={relationshipLabel}
        />
      </form>
    </Dialog>
  );
}

function EditMembershipDialog({
  person,
  busy,
  onClose,
  onSave,
}: {
  readonly person: contracts.PersonMembershipView;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onSave: (draft: UpdateMembershipDraft) => Promise<void>;
}) {
  const [preset, setPreset] = useState<contracts.MembershipPreset>(() => presetFor(person));
  const [capabilities, setCapabilities] = useState<readonly Capability[]>(
    person.capabilities ?? [],
  );

  return (
    <Dialog
      footer={
        <>
          <Button disabled={busy} onClick={onClose}>
            {COPY.cancelAction}
          </Button>
          <Button disabled={busy} form="people-membership-form" type="submit" variant="primary">
            {COPY.saveCapabilitiesAction}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={COPY.editCapabilitiesTitle}
    >
      <form
        className="people__form"
        id="people-membership-form"
        onSubmit={(event) => {
          event.preventDefault();
          void onSave({ personId: person.personId, preset, capabilities });
        }}
      >
        <MembershipFields
          capabilities={capabilities}
          onCapabilitiesChange={setCapabilities}
          onPresetChange={(nextPreset) => {
            setPreset(nextPreset);
            setCapabilities(contracts.capabilitiesForPreset(nextPreset));
          }}
          preset={preset}
        />
      </form>
    </Dialog>
  );
}

function MembershipFields({
  preset,
  capabilities,
  onPresetChange,
  onCapabilitiesChange,
}: {
  readonly preset: contracts.MembershipPreset;
  readonly capabilities: readonly Capability[];
  readonly onPresetChange: (preset: contracts.MembershipPreset) => void;
  readonly onCapabilitiesChange: (capabilities: readonly Capability[]) => void;
}) {
  const changePreset = (event: ChangeEvent<HTMLSelectElement>) => {
    const parsed = contracts.membershipPresetSchema.safeParse(event.target.value);
    if (parsed.success) onPresetChange(parsed.data);
  };

  return (
    <>
      <Select
        hint={COPY.presetHint}
        label={COPY.presetLabel}
        onChange={changePreset}
        options={PRESET_OPTIONS}
        value={preset}
      />
      <details className="people__capabilities">
        <summary>{COPY.fineGrainedSummary}</summary>
        <p>{COPY.fineGrainedHint}</p>
        <fieldset>
          <legend>{COPY.capabilitiesLegend}</legend>
          {ALL_CAPABILITIES.map((capability) => (
            <label className="people__checkbox" key={capability}>
              <input
                checked={capabilities.includes(capability)}
                onChange={() => {
                  onCapabilitiesChange(
                    capabilities.includes(capability)
                      ? capabilities.filter((candidate) => candidate !== capability)
                      : [...capabilities, capability],
                  );
                }}
                type="checkbox"
              />
              {capabilityLabel(capability)}
            </label>
          ))}
        </fieldset>
      </details>
    </>
  );
}

const CAPABILITY_LABELS: Readonly<Record<Capability, string>> = {
  ...COPY.capabilities,
  ...PRACTICAL_UPDATE_CAPABILITY_MESSAGES,
};

function capabilityLabel(capability: Capability): string {
  return CAPABILITY_LABELS[capability];
}

function DeactivateDialog({
  person,
  busy,
  onClose,
  onDeactivate,
}: {
  readonly person: contracts.PersonMembershipView;
  readonly busy: boolean;
  readonly onClose: () => void;
  readonly onDeactivate: (personId: string) => Promise<void>;
}) {
  return (
    <Dialog
      footer={
        <>
          <Button disabled={busy} onClick={onClose}>
            {COPY.keepActiveAction}
          </Button>
          <Button
            disabled={busy}
            onClick={() => {
              void onDeactivate(person.personId);
            }}
            variant="danger"
          >
            {COPY.confirmDeactivateAction}
          </Button>
        </>
      }
      onClose={onClose}
      open
      title={COPY.deactivateTitle}
    >
      <p>{COPY.deactivateBody}</p>
      {person.futureCommitmentCount === 0 ? null : (
        <StatusMessage tone="warning">
          {COPY.futureCommitments(person.futureCommitmentCount)}
        </StatusMessage>
      )}
      <p>{COPY.deactivateHistoryNotice}</p>
    </Dialog>
  );
}

function OutcomeMessage({ outcome }: { readonly outcome: PeopleOutcome }) {
  if (outcome.status === "idle" || outcome.status === "working") return null;
  if (outcome.status === "refused") {
    return <StatusMessage tone="error">{outcome.message}</StatusMessage>;
  }
  return <StatusMessage tone="success">{COPY.saved[outcome.action]}</StatusMessage>;
}

/**
 * What this card calls somebody (PER-014).
 *
 * `capabilities` is `null` — WITHHELD, not empty — for any reader without
 * `people.manage`; `listPeople` returns the array only to somebody who may edit it.
 * Reading that absence as "holds nothing" made `presetFor` answer "viewsOnly" for
 * every non-organiser, so an ordinary member's People list called all their helpers
 * View only. An unknown grant is not a view-only one, and PER-014 asks this list to
 * say what is true in plain language — so without the array the SERVER's role label
 * is what stands, and the view-only refinement is made only where it can be known.
 */
function roleLabelFor(person: contracts.PersonMembershipView): string {
  if (person.capabilities === null) return COPY.roles[person.role];
  return presetFor(person) === "viewsOnly" ? COPY.presets.viewsOnly : COPY.roles[person.role];
}

function presetFor(person: contracts.PersonMembershipView): contracts.MembershipPreset {
  if (person.role === "organiser") return "organiser";
  const capabilities = person.capabilities ?? [];
  return capabilities.includes(CAPABILITIES.AVAILABILITY_RECORD_SELF) ||
    capabilities.includes(CAPABILITIES.COMMITMENT_ACCEPT_SELF)
    ? "helper"
    : "viewsOnly";
}
